<template>
  <div
    :role="appSettingsStore.availableCompanies.length ? 'button' : undefined"
    :tabindex="appSettingsStore.availableCompanies.length ? 0 : undefined"
    class="sidebar-brand-btn"
    :class="{ 'sidebar-brand-btn--static': !appSettingsStore.availableCompanies.length }"
    @click="onClick"
    @keydown.enter.space.prevent="onActivate">
    <div class="sidebar-brand-btn__content">
      <q-avatar
        square
        size="34px"
        :color="avatarColor"
        text-color="white"
        class="sidebar-brand-btn__badge">
        {{ initial }}
      </q-avatar>
      <div class="sidebar-brand-btn__text">
        <div class="sidebar-brand-btn__name">{{ name }}</div>
        <div
          v-if="shortLabel && shortLabel !== name"
          class="sidebar-brand-btn__sub">
          {{ shortLabel }}
        </div>
      </div>
      <div
        v-if="$slots.actions"
        class="sidebar-brand-btn__actions"
        @click.stop
        @keydown.stop>
        <slot name="actions" />
      </div>
      <q-icon
        v-if="appSettingsStore.availableCompanies.length"
        :name="soleOtherCompany ? symRoundedSwapHoriz : symRoundedUnfoldMore"
        class="sidebar-brand-btn__swap" />
    </div>

    <q-tooltip v-if="soleOtherCompany">{{ $t('crudmanager.sidebar.switchCompany') }} — {{ soleOtherCompany.name || soleOtherCompany.menu_label }}</q-tooltip>

    <q-menu
      v-if="appSettingsStore.availableCompanies.length > 1"
      auto-close
      anchor="bottom middle"
      self="top middle"
      :offset="[0, 4]"
      class="sidebar-brand-menu">
      <q-list padding>
        <q-item-label
          header
          class="sidebar-brand-menu__header">
          {{ $t('crudmanager.sidebar.switchCompany') }}
        </q-item-label>
        <q-item
          v-for="company in appSettingsStore.availableCompanies"
          :key="company.id"
          v-close-popup
          clickable
          :active="company.id === appSettingsStore.currentCompany?.id"
          active-class="sidebar-brand-menu__active"
          class="sidebar-brand-menu__item"
          @click="appSettingsStore.setCurrentCompany(company)">
          <q-item-section avatar>
            <q-avatar
              square
              size="28px"
              :class="`sidebar-brand-menu__badge bg-${company?.header_color ?? 'primary'}`">
              {{ (company?.menu_label || company.name || '?').trim().charAt(0).toUpperCase() }}
            </q-avatar>
          </q-item-section>
          <q-item-section>
            <q-item-label>{{ company?.name || company?.menu_label }}</q-item-label>
            <q-item-label
              v-if="company?.menu_label && company?.menu_label !== company?.name"
              caption>
              {{ company.menu_label }}
            </q-item-label>
          </q-item-section>
          <q-item-section
            v-if="company.id === appSettingsStore.currentCompany?.id"
            side>
            <q-icon
              :name="symRoundedCheck"
              color="primary" />
          </q-item-section>
        </q-item>
      </q-list>
    </q-menu>
  </div>
</template>

<script setup lang="ts">
import { symRoundedCheck, symRoundedSwapHoriz, symRoundedUnfoldMore } from '@quasar/extras/material-symbols-rounded'
import { useAppSettingsStore } from 'src/CrudManager/Core/stores/useAppSettingsStore'
import { computed } from 'vue'

const appSettingsStore = useAppSettingsStore()

const name = computed(() => appSettingsStore.currentCompany?.name || appSettingsStore.currentCompany?.menu_label || 'App')
const shortLabel = computed(() => appSettingsStore.currentCompany?.menu_label || '')
const initial = computed(() => (name.value || '?').trim().charAt(0).toUpperCase())
const avatarColor = computed(() => appSettingsStore.currentCompany?.header_color || 'primary')

// Exactly one other company: the button toggles directly instead of opening the menu
const soleOtherCompany = computed(() => (appSettingsStore.availableCompanies.length === 1 ? appSettingsStore.availableCompanies[0] : null))

const onClick = () => {
  if (soleOtherCompany.value) {
    appSettingsStore.setCurrentCompany(soleOtherCompany.value)
  }
}

const onActivate = (event: KeyboardEvent) => {
  ;(event.currentTarget as HTMLElement | null)?.click()
}
</script>

<style lang="scss" scoped>
.sidebar-brand-btn {
  margin: 0 0 6px;
  padding: 6px 8px;
  width: 100%;
  border-radius: 0;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(0, 0, 0, 0.45);
  min-height: 48px;
  cursor: pointer;
  user-select: none;
  outline: none;
  transition:
    background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  &:hover {
    background: rgba(0, 0, 0, 0.45);
    border-color: rgba(0, 0, 0, 0.55);
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px var(--q-primary);
  }

  &--static {
    cursor: default;
  }
}

.sidebar-brand-btn__actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex: 1 1 auto;
  min-width: 0;
}

.body--light .sidebar-brand-btn {
  background: rgba(0, 0, 0, 0.03);
  border-color: rgba(0, 0, 0, 0.06);

  &:hover {
    background: rgba(0, 0, 0, 0.06);
    border-color: rgba(0, 0, 0, 0.1);
  }
}

.sidebar-brand-btn__content {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.sidebar-brand-btn__badge {
  font-weight: 700;
  font-size: 1.05rem;
  border-radius: 0;
  flex-shrink: 0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.sidebar-brand-btn__text {
  flex: 0 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
}

.sidebar-brand-btn__name {
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: -0.005em;
  line-height: 1.2;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-brand-btn__sub {
  font-size: 0.68rem;
  color: $grey-6;
  line-height: 1.2;
  margin-top: 1px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-brand-btn__swap {
  font-size: 1.15rem;
  opacity: 0.55;
  flex-shrink: 0;
  transition: opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar-brand-btn:hover .sidebar-brand-btn__swap {
  opacity: 0.9;
}
</style>

<style lang="scss">
.sidebar-brand-menu {
  min-width: 240px;
  border-radius: 12px;
}

.sidebar-brand-menu__header {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  opacity: 0.65;
  padding: 8px 16px 4px;
}

.sidebar-brand-menu__item {
  min-height: 44px;
  border-radius: 8px;
  margin: 0 4px;
}

.sidebar-brand-menu__active {
  background: color-mix(in srgb, var(--q-primary) 12%, transparent) !important;
  color: var(--q-primary);
}

.sidebar-brand-menu__badge {
  font-weight: 700;
  font-size: 0.85rem;
  border-radius: 6px;
  color: #000000;
}
</style>
